import httpClient from 'utility/httpClient.util'
import { normalizePatientDrugAllergy } from './drugAllergy.util'

const ACTIVE_BENEFIT_TYPE_API = '/core/benefit-type?isActive=1&sort.benefitTypeCode=ASC&page=1&perPage=1000'
const SELF_PAY_BENEFIT_TYPE_CODE = '10'

export const unwrapData = (response) => response?.data?.data ?? null

export const unwrapList = (response) => {
  const data = unwrapData(response)
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.items)) return data.items
  return data ? [data] : []
}

export const getApiErrorMessage = (error) => error?.response?.data?.message || error?.response?.data?.messages?.join?.(', ') || error?.message || 'ไม่สามารถเชื่อมต่อระบบได้'

export const searchHospitals = (searchKey) =>
  httpClient.get(
    `/core/hospital/search?searchKey=${encodeURIComponent(searchKey || '')}&page=1&perPage=30`
  )

const encode = (value) => encodeURIComponent(value || '')

const today = () => new Date().toISOString().slice(0, 10)

const isSelfPayBenefit = (benefit = {}) =>
  String((benefit || {}).benefitTypeCode || '') === SELF_PAY_BENEFIT_TYPE_CODE

const normalizePrimaryBenefitForSave = (primaryBenefit = {}, isNewRegistration) => {
  const benefit = primaryBenefit || {}
  if (
    isNewRegistration &&
    isSelfPayBenefit(benefit) &&
    !benefit.startDate
  ) {
    return { ...benefit, startDate: today() }
  }
  return benefit
}

const pick = (source, keys) =>
  keys.reduce((result, key) => {
    if (source?.[key] !== undefined) result[key] = source[key]
    return result
  }, {})

const omitEmpty = (source) =>
  Object.entries(source).reduce((result, [key, value]) => {
    if (value !== '' && value !== undefined) {
      result[key] = value
    }
    return result
  }, {})

const withNullableFields = (source, fields) =>
  fields.reduce((result, field) => {
    if (!Object.prototype.hasOwnProperty.call(result, field)) return result
    if (
      result[field] === '' ||
      (typeof result[field] === 'string' && !result[field].trim())
    ) {
      result[field] = null
    }
    return result
  }, { ...source })

const withNullableIntegerFields = (source, fields) =>
  fields.reduce((result, field) => {
    if (!Object.prototype.hasOwnProperty.call(result, field)) return result

    const value = result[field]
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && !value.trim())
    ) {
      result[field] = null
      return result
    }

    const numericValue = Number(value)
    if (Number.isInteger(numericValue)) result[field] = numericValue
    return result
  }, { ...source })

const PERSON_FIELDS = [
  'personId',
  'prefixName',
  'firstName',
  'middleName',
  'lastName',
  'prefixNameEn',
  'firstNameEn',
  'middleNameEn',
  'lastNameEn',
  'aliasName',
  'occupationCode',
  'nationalityCode',
  'citizenshipCode',
  'birthDate',
  'birthTime',
  'passportNumber',
  'maritalStatusId',
  'religionCode',
  'genderId',
  'citizenId',
  'cardExpiredDate',
  'foreignCitizenId',
  'phoneNumber',
  'secondPhoneNumber',
  'communicationLanguageCode',
  'email',
  'sexualOrientation',
  'servicePreference',
  'isFakePerson',
  'isFakeName',
  'isFakeBirthday',
  'isFakeCitizenId',
  'mediaAttachmentId'
]

const PATIENT_FIELDS = [
  'personId',
  'nmdHn',
  'militaryNumber',
  'privateSoldierId',
  'navyPatientTypeId',
  'navyPatientGroupId',
  'bloodGroupCode',
  'bloodGroupRh',
  'benefitTypeCode',
  'drugAllergy',
  'drugAllergicReaction',
  'concealedName',
  'concealedNote',
  'physicalDescription',
  'referralChannelId',
  'patientClassId',
  'isUnknownAdr',
  'isNoAdr',
  'isAdr',
  'isInBenefitRegion',
  'isInCups',
  'isDeceased',
  'isAnonymousPerson',
  'isHospitalOfficer',
  'isDocumentDestroyed',
  'isAssumedHn',
  'deceasedDate',
  'deceasedTime',
  'attachmentId'
]

const PERSON_INTEGER_FIELDS = [
  'personId',
  'maritalStatusId',
  'genderId',
  'isFakePerson',
  'isFakeName',
  'isFakeBirthday',
  'isFakeCitizenId'
]

const PATIENT_INTEGER_FIELDS = [
  'personId',
  'referralChannelId',
  'patientClassId',
  'navyPatientTypeId',
  'navyPatientGroupId',
  'isUnknownAdr',
  'isNoAdr',
  'isAdr',
  'isInBenefitRegion',
  'isInCups',
  'isDeceased',
  'isAnonymousPerson',
  'isHospitalOfficer',
  'isDocumentDestroyed',
  'isAssumedHn'
]

const ADDRESS_FIELDS = [
  'personAddressId',
  'personId',
  'houseNumber',
  'villageCode',
  'roadName',
  'subDistrictCode',
  'districtCode',
  'provinceCode',
  'postalCode',
  'countryCode',
  'phoneNumber',
  'isPrimary',
  'isEditable',
  'isInCups',
  'isRegiment',
  'isInBenefitRegion',
  'isSameCitizenAddress'
]

const BENEFIT_FIELDS = [
  'patientBenefitTypeId',
  'seq',
  'startDate',
  'expiredDate',
  'benefitTypeCode',
  'hospitalMainCode',
  'hospitalSubCode',
  'benefitNumber',
  'sponsorId',
  'benefitPriceCategoryId',
  'policyNo',
  'policyHolderName',
  'relationshipToHolderId',
  'coverageType',
  'benefitLimit',
  'perVisitLimit',
  'roomLimitPerDay',
  'approvalBy',
  'note'
]

const CONTACT_FIELDS = [
  'deleteMode',
  'drugContractPriority',
  'displayOrder',
  'isRelationshipAddress',
  'personId',
  'firstName',
  'lastName',
  'middleName',
  'homeAddress',
  'genderId',
  'citizenId',
  'phoneNumber',
  'age',
  'relationshipTypeCode',
  'contactRole'
]

const CONTACT_INTEGER_FIELDS = [
  'deleteMode',
  'drugContractPriority',
  'displayOrder',
  'isRelationshipAddress',
  'personId',
  'genderId',
  'age'
]

const PERSON_TEMPORAL_FIELDS = ['birthDate', 'birthTime', 'cardExpiredDate']
const PATIENT_TEMPORAL_FIELDS = ['deceasedDate', 'deceasedTime']
const BENEFIT_DATE_FIELDS = ['startDate', 'expiredDate']
const DECEASED_TEMPORAL_FIELDS = [
  'deceasedDate',
  'deceasedTime',
  'deathCertificateDate'
]
const NAME_CHANGE_DATE_FIELDS = ['changedDate']
const CONTACT_DATA_FIELDS = [
  'firstName',
  'middleName',
  'lastName',
  'genderId',
  'citizenId',
  'relationshipTypeCode',
  'contactRole',
  'homeAddress',
  'phoneNumber'
]

const hasContactRecordId = (contact = {}) =>
  Boolean(
    contact.personId ||
      contact.personRelationshipId ||
      contact.personRelationship?.personRelationshipId
  )

const hasContactValue = (contact = {}) =>
  CONTACT_DATA_FIELDS.some((key) => {
    const value = contact[key]
    return value !== undefined && value !== null && String(value).trim() !== ''
  }) || Number(contact.isRelationshipAddress) === 1

const getContactsForSave = (contacts = []) =>
  contacts.filter(
    (contact) =>
      Number(contact.deleteMode) === 1 ||
      hasContactRecordId(contact) ||
      hasContactValue(contact)
  )

const safeRequest = async (name, request) => {
  try {
    return { name, response: await request(), error: null }
  } catch (error) {
    return { name, response: null, error }
  }
}

export const searchPatients = (searchKey) =>
  httpClient.get(`/pas/patient/search?searchKey=${encode(searchKey)}&include=person,personAddress,attachment,patientAttachment.Attachment,patientAttachment.MediaGroup&page=1&perPage=20`)

export const searchPatientsForRegister = (field, searchKey, page = 1, perPage = 10) =>
  httpClient.get(`/pas/patient/search?${field}=${encode(searchKey)}&include=person&page=${page}&perPage=${perPage}`)

export const searchPatientVisitsByHn = (hn) =>
  httpClient.get('/clinical/patient-visit/findByHn', { params: { hn, include: 'doctor,patient.person' } })

export const searchPatientAdmissionsByHn = (hn) =>
  httpClient.get(`/ipd/in-patient-admit/history/${encode(hn)}`)

export const searchPatientAdmissionByAn = (an) =>
  httpClient.get('/ipd/in-patient-admit/profile', { params: { an, include: 'patient.person,Ward' } })

export const searchPatientsByFirstName = (firstName) =>
  httpClient.get(`/pas/patient/search?firstName=${encode(firstName)}&include=person&page=1&perPage=20`)

export const searchPatientByCitizenId = (citizenId) => httpClient.get(`/pas/patient/search?cId=${encode(citizenId)}&include=person&page=1&perPage=20`)

export const searchPatientByHn = (hn) => httpClient.get(`/pas/patient/search?hn=${encode(hn)}&exactMatch=true&page=1&perPage=100`)

export const CUSTOM_HN_CONFLICT_CODE = 'CUSTOM_HN_CONFLICT'
export const MANUAL_HN_REQUIRED_CODE = 'MANUAL_HN_REQUIRED'

export const assertCustomHnAvailable = async (hn) => {
  const normalizedHn = String(hn || '').trim()
  const response = await searchPatientByHn(normalizedHn)
  const duplicate = unwrapList(response).some(
    (patient) =>
      String(patient?.hn || patient?.patient?.hn || '').trim().toLowerCase() ===
      normalizedHn.toLowerCase()
  )
  if (duplicate) {
    const error = new Error(`HN ${normalizedHn} มีผู้ป่วยใช้งานแล้ว`)
    error.code = CUSTOM_HN_CONFLICT_CODE
    throw error
  }
}

export const promotePatientBenefit = (patientBenefitTypeId) =>
  httpClient.put(
    `/pas/patient-benefit-type/${encode(patientBenefitTypeId)}/set-primary-benefit-type`
  )

export const createPatientHn = () => httpClient.post('/pas/patient/register')

export const fetchSuggestedHn = () => httpClient.get('/core/serial/monitor/HN?detail=compact')

export const preparePatientHnTarget = (payload) =>
  httpClient.post('/pas/patient-hn-merge/prepare-target', payload)

export const previewPatientHnMerge = (payload) =>
  httpClient.post('/pas/patient-hn-merge/preview', payload)

export const executePatientHnMerge = (payload) =>
  httpClient.post('/pas/patient-hn-merge/execute', payload)

export const fetchPatientHnMergeAudit = (personId) =>
  httpClient.get(`/pas/patient-hn-merge/audit?personId=${encode(personId)}&limit=100&offset=0`)

export const nextFakeCitizenId = () => httpClient.get('/core/serial/nextval/FAKE_PERSON_CID')

export const fetchCommonDiseases = () => httpClient.get('/core/underlying-disease?page=1&perPage=15')

export const fetchRegistration = async (hn, options = {}) => {
  const includeHnAudit = options.includeHnAudit === true
  const patientHn = encode(hn)
  const requests = [
    safeRequest('patient', () => httpClient.get(`/pas/patient/${patientHn}?include=person,attachment,patientAttachment.Attachment,patientAttachment.MediaGroup`)),
    safeRequest('person', () => httpClient.get(`/pas/person/hn/${patientHn}`)),
    safeRequest('benefits', () => httpClient.get(`/pas/patient-benefit-type/hn/${patientHn}?include=benefitType,hospitalMain,hospitalSub,sponsor,benefitPriceCategory`)),
    safeRequest('contacts', () => httpClient.get(`/pas/person/patient/${patientHn}/relationship`)),
    safeRequest('notes', () => httpClient.get(`/clinical/patient-note?hn=${patientHn}`)),
    safeRequest('chronicDiseases', () => httpClient.get(`/clinical/patient-chronic-disease/hn/${patientHn}`)),
    safeRequest('allergies', () => httpClient.get(`/adr/patient-allergy/hn/${patientHn}`))
  ]

  const result = await Promise.all(requests)
  const benefitResult = result.find((item) => item.name === 'benefits')
  if (!benefitResult.error && !unwrapList(benefitResult.response).filter(Boolean).length) {
    result.push(
      await safeRequest('defaultBenefitConfig', () =>
        httpClient.get('/core/config/configCode/patient_register_default_benefit', { cache: false })
      )
    )
  }
  const person = unwrapList(result.find((item) => item.name === 'person')?.response)[0]

  if (person?.personId) {
    result.push(
      await safeRequest('regiment', () => httpClient.get(`/pas/person-regiment/${encode(person.personId)}`)),
      await safeRequest('nameChanges', () => httpClient.get(`/pas/person-name-change-history/${encode(person.personId)}?include=createdByUser`))
    )
    if (includeHnAudit) {
      result.push(
        await safeRequest('hnChanges', () => fetchPatientHnMergeAudit(person.personId))
      )
    }
  }

  return result
}

export const fetchRegistrationTab = async (hn, tabKey) => {
  const patientHn = encode(hn)
  const requestMap = {
    address: () =>
      ['CITIZEN', 'CURRENT', 'ORIGIN', 'WORKPLACE'].map(
        (addressTypeCode) =>
          safeRequest(`address.${addressTypeCode}`, () =>
            httpClient.get(
              `/pas/person-address/hn/${patientHn}?addressTypeCode=${addressTypeCode}&include=addressCountry,addressDistrict,addressProvince,addressSubdistrict`
            )
          )
      ),
    familyHistory: () => [
      safeRequest('familyHistory', () =>
        httpClient.get(`/pas/patient-family-medical-history/${patientHn}`)
      )
    ],
    lifeStatus: () => [
      safeRequest('deceased', () =>
        httpClient.get(`/pas/deceased/${patientHn}`)
      )
    ],
    patientNetwork: () => [
      safeRequest('patientNetwork', () =>
        httpClient.get(
          `/pas/patient-network/hn/${patientHn}?include=patient.person,relatedPatient.person`
        )
      )
    ]
  }

  return Promise.all(requestMap[tabKey]?.() || [])
}

export const fetchLookups = async (lookupKeys) => {
  const requestMap = {
    prefixNames: () => httpClient.get('/core/prefix-name?langCode=th-TH&page=1&perPage=1000'),
    prefixNamesEn: () => httpClient.get('/core/prefix-name?langCode=en-US&page=1&perPage=1000'),
    genders: () => httpClient.get('/core/gender?page=1&perPage=1000'),
    maritalStatuses: () => httpClient.get('/core/martial-status?page=1&perPage=1000'),
    occupations: () => httpClient.get('/core/occupation?page=1&perPage=1000'),
    nationalities: () => httpClient.get('/core/nationality?page=1&perPage=1000'),
    religions: () => httpClient.get('/core/religion?page=1&perPage=1000'),
    referralChannels: () => httpClient.get('/pas/referral-channel?isActive=1&page=1&perPage=1000'),
    patientClasses: () => httpClient.get('/core/patient-class?page=1&perPage=1000'),
    navyPatientTypes: () => httpClient.get('/pas/navy-patient-type?isActive=1&page=1&perPage=1000'),
    relationshipTypes: () => httpClient.get('/pas/relationship-type?page=1&perPage=1000'),
    benefitTypes: () => httpClient.get(ACTIVE_BENEFIT_TYPE_API),
    benefitPriceCategories: () => httpClient.get('/core/benefit-price-category?page=1&perPage=1000'),
    sponsors: () => httpClient.get('/core/sponsor?page=1&perPage=1000'),
    sponsorTypes: () => httpClient.get('/core/sponsor-type?page=1&perPage=1000'),
    mainRegiments: () => httpClient.get('/pas/regiments?regimentLevel=1&page=1&perPage=1000'),
    addressProvinces: () => httpClient.get('/core/address-province?page=1&perPage=1000'),
    addressCountries: () => httpClient.get('/core/address-country?page=1&perPage=1000'),
    employerTypes: () => httpClient.get('/pas/employer-type?page=1&perPage=1000')
  }

  const requestedKeys = Array.isArray(lookupKeys)
    ? lookupKeys.filter((key) => requestMap[key])
    : Object.keys(requestMap)

  const entries = await Promise.all(
    requestedKeys.map(async (key) => {
      const request = requestMap[key]
      const result = await safeRequest(key, request)
      const records = result.error ? [] : unwrapList(result.response)
      return [
        key,
        key === 'benefitTypes'
          ? records.filter((record) => Number(record?.isActive) === 1)
          : records
      ]
    })
  )

  return Object.fromEntries(entries)
}

export const fetchSubRegiments = (parentRegimentId) => {
  const parsedRegimentId = Number(parentRegimentId)

  if (!Number.isInteger(parsedRegimentId) || parsedRegimentId <= 0) {
    return Promise.resolve({ data: { data: [] } })
  }

  return httpClient.get(`/pas/regiments?regimentLevel=2&parentRegimentId=${encode(parsedRegimentId)}&page=1&perPage=1000`)
}

export const fetchNavyPatientGroups = (navyPatientTypeId) => {
  const parsedNavyPatientTypeId = Number(navyPatientTypeId)

  if (!Number.isInteger(parsedNavyPatientTypeId) || parsedNavyPatientTypeId <= 0) {
    return Promise.resolve({ data: { data: [] } })
  }

  return httpClient.get(`/pas/navy-patient-group?navyPatientTypeId=${encode(parsedNavyPatientTypeId)}&isActive=1&page=1&perPage=1000`)
}

export const fetchAddressDistricts = (provinceCode) => httpClient.get(`/core/address-district?provinceCode=${encode(provinceCode)}&page=1&perPage=1000`)

export const fetchAddressSubdistricts = (provinceCode, districtCode) =>
  httpClient.get(`/core/address-subdistrict?provinceCode=${encode(provinceCode)}&districtCode=${encode(districtCode)}&page=1&perPage=1000`)

export const uploadAttachment = async ({ hn, file, mediaGroupCode }) => {
  if (!file) return null

  const formData = new FormData()
  formData.append('file', file)
  const uploadResponse = await httpClient.post(`/media/attachment/upload/${mediaGroupCode}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  const attachment = unwrapList(uploadResponse)[0]
  if (!attachment) throw new Error('อัปโหลดไฟล์ไม่สำเร็จ')

  await httpClient.post('/pas/patient-attachment', {
    hn,
    mediaCode: '',
    attachmentId: attachment.attachmentId,
    mediaGroupId: attachment.mediaGroupId,
    attachmentName: file.name
  })

  return attachment
}

export const fetchAttachmentBlob = async (attachmentId) => {
  const response = await httpClient.get(
    `/media/attachment/originalFile/${encode(attachmentId)}`,
    { responseType: 'blob' }
  )
  return response.data instanceof Blob
    ? response.data
    : new Blob([response.data])
}

export const fetchPatientProfileImages = (hn) =>
  httpClient.get(
    `/pas/patient/${encode(
      hn
    )}?include=patientAttachment.Attachment,patientAttachment.MediaGroup`
  )

const putAddress = (hn, addressType, data) => httpClient.put(`/pas/person-address/hn/${encode(hn)}/${String(addressType || '').toLowerCase()}`, pick(data, ADDRESS_FIELDS))

export const saveRegistration = async ({ form, startedAt, dirtySections, profileImage, profileAudio, customHn, manualHnRequired = false, onHnCreated }) => {
  const completed = []
  const extensionErrors = []
  let hn = form.hn
  const normalizedCustomHn = String(customHn || '').trim()
  const isCustomHnRegistration = Boolean(normalizedCustomHn)
  const isNewRegistration = !hn || isCustomHnRegistration
  const primaryBenefitForSave = normalizePrimaryBenefitForSave(
    form.primaryBenefit,
    isNewRegistration
  )
  const isDirty = (key) => Boolean(dirtySections?.[key])

  const runCore = async (name, request) => {
    const response = await request()
    completed.push(name)
    return response
  }

  const runExtension = async (name, request) => {
    try {
      const response = await request()
      completed.push(name)
      return response
    } catch (error) {
      extensionErrors.push({ name, error })
      return null
    }
  }

  if (isCustomHnRegistration) {
    await assertCustomHnAvailable(normalizedCustomHn)
    hn = normalizedCustomHn
  } else if (!hn) {
    if (manualHnRequired) {
      const error = new Error('กรุณาระบุ HN ด้วยตนเอง')
      error.code = MANUAL_HN_REQUIRED_CODE
      throw error
    }
    const registerResponse = await runCore('สร้าง HN', () => createPatientHn())
    hn = unwrapData(registerResponse)?.hn
    if (!hn) throw new Error('ระบบไม่ได้ส่ง HN กลับมา')
    onHnCreated?.(hn)
  }

  const personPayload = withNullableIntegerFields(
    withNullableFields(
      pick(form.person, PERSON_FIELDS),
      PERSON_TEMPORAL_FIELDS
    ),
    PERSON_INTEGER_FIELDS
  )
  const personResponse = await runCore('ข้อมูลบุคคล', () =>
    httpClient.put(`/pas/person/hn/${encode(hn)}`, personPayload)
  )
  const savedPerson = unwrapList(personResponse)[0] || {}
  const personId = savedPerson.personId || form.person.personId
  const patientForSave = normalizePatientDrugAllergy(form.patient)

  await runCore('ข้อมูลผู้ป่วย', () =>
    httpClient.put(`/pas/patient/${encode(hn)}`, {
      ...withNullableIntegerFields(
        withNullableFields(
          pick({ ...patientForSave, personId }, PATIENT_FIELDS),
          PATIENT_TEMPORAL_FIELDS
        ),
        PATIENT_INTEGER_FIELDS
      ),
      benefitTypeCode: primaryBenefitForSave?.benefitTypeCode || patientForSave.benefitTypeCode
    })
  )

  if (isCustomHnRegistration) onHnCreated?.(hn)

  if (primaryBenefitForSave?.benefitTypeCode && (isNewRegistration || isDirty('primaryBenefit'))) {
    await runCore('สิทธิการรักษาหลัก', () =>
      httpClient.put(`/pas/patient-benefit-type/primary/hn/${encode(hn)}/registration`, {
        ...omitEmpty(
          withNullableFields(
            pick(primaryBenefitForSave, BENEFIT_FIELDS),
            BENEFIT_DATE_FIELDS
          )
        ),
        patientBenefitTypeId: primaryBenefitForSave.patientBenefitTypeId || undefined
      })
    )
  }

  await Promise.all(
    Object.entries(form.addresses)
      .filter(([addressType]) => isDirty(`address.${addressType}`))
      .map(([addressType, address]) => runExtension(`ที่อยู่ ${addressType}`, () => putAddress(hn, addressType, { ...address, personId })))
  )

  if (isDirty('secondaryBenefits')) {
    await runExtension('สิทธิการรักษารอง', () =>
      httpClient.put(
        `/pas/patient-benefit-type/hn/${encode(hn)}`,
        form.secondaryBenefits.map((benefit) =>
          withNullableFields(
            pick(benefit, BENEFIT_FIELDS),
            BENEFIT_DATE_FIELDS
          )
        )
      )
    )
  }

  if (isDirty('contacts')) {
    await runExtension('ผู้ติดต่อ', () =>
      httpClient.put(`/pas/person/patient/${encode(hn)}/relationship/bulk`, {
        items: getContactsForSave(form.contacts).map((contact) =>
          withNullableIntegerFields(
            pick(contact, CONTACT_FIELDS),
            CONTACT_INTEGER_FIELDS
          )
        )
      })
    )
  }

  if (personId && isDirty('regiment')) {
    await runExtension('สังกัด', () => httpClient.put(`/pas/person-regiment/${encode(personId)}`, form.regiment))
  }

  const optionalSections = [
    ['familyHistory', 'ประวัติครอบครัว', `/pas/patient-family-medical-history/${encode(hn)}`],
    ['notes', 'บันทึกผู้ป่วย', `/clinical/patient-note/hn/${encode(hn)}/bulk`],
    ['allergies', 'ข้อมูลแพ้ยา', `/adr/patient-allergy/hn/${encode(hn)}`],
    ['patientNetwork', 'Patient Network', `/pas/patient-network/hn/${encode(hn)}`],
    ['chronicDiseases', 'โรคประจำตัว', `/clinical/patient-chronic-disease/hn/${encode(hn)}`]
  ]

  for (const [key, label, url] of optionalSections) {
    if (isDirty(key)) {
      await runExtension(label, () => httpClient.put(url, form[key]))
    }
  }

  if (isDirty('deceased') && Boolean(Number(form.patient.isDeceased))) {
    await runExtension('ข้อมูลสถานภาพ', () =>
      httpClient.put(
        `/pas/deceased/${encode(hn)}`,
        withNullableFields(form.deceased, DECEASED_TEMPORAL_FIELDS)
      )
    )
  }

  if (isDirty('nameChanges') && personId) {
    await runExtension('ประวัติเปลี่ยนชื่อ', () =>
      httpClient.put(
        `/pas/person-name-change-history/${encode(personId)}`,
        form.nameChanges.map((item) =>
          withNullableFields(item, NAME_CHANGE_DATE_FIELDS)
        )
      )
    )
  }

  if (profileImage) {
    await runExtension('รูปผู้ป่วย', () =>
      uploadAttachment({
        hn,
        file: profileImage,
        mediaGroupCode: 'PATIENT_PROFILE_IMAGE'
      })
    )
  }

  if (profileAudio) {
    await runExtension('เสียงผู้ป่วย', () =>
      uploadAttachment({
        hn,
        file: profileAudio,
        mediaGroupCode: 'PATIENT_PROFILE_AUDIO'
      })
    )
  }

  if (isNewRegistration) {
    await runExtension('เวลาเริ่มลงทะเบียน', () =>
      httpClient.post('/clinical/patient-journey-stage/opd-register/start', {
        hn,
        startedAt,
        trigger: 'opd-register'
      })
    )
  }

  return { hn, completed, extensionErrors }
}
